import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDownloadSettings } from '../hooks/use-download-settings.js';
import { ALL_SECTIONS } from '../constants.js';

function DownloadSettingsTab() {
  const { t } = useTranslation();
  const { download, isLoading, error, success } = useDownloadSettings();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const SECTION_KEY_MAP = {
    settings_system: 'saveRestore.sectionGeneralSettings',
    settings_agreements: 'saveRestore.sectionAgreements',
    settings_email_credentials: 'saveRestore.sectionEmailCredentials',
    settings_bank_credentials: 'saveRestore.sectionBankCredentials',
    settings_crypto_credentials: 'saveRestore.sectionCryptoCredentials',
    settings_trading_servers: 'saveRestore.sectionTradingServers',
    settings_trading_accounts: 'saveRestore.sectionTradingAccounts',
    settings_trading_platform_alias: 'saveRestore.sectionTradingPlatformAlias',
    jurisdictions: 'saveRestore.sectionJurisdictions',
    kyc_steps: 'saveRestore.sectionKycSteps',
    kyc_fields: 'saveRestore.sectionKycFields',
    kyc_field_dependencies: 'saveRestore.sectionKycFieldDependencies',
    email_templates: 'saveRestore.sectionEmailTemplates',
    email_template_wraps: 'saveRestore.sectionEmailTemplateWraps',
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    download({ password, passwordConfirm });
  };

  const isValid = password.length >= 8 && password === passwordConfirm;

  return (
    <div className="row">
      <div className="col-md-6">
        <h5 className="mb-3">{t('saveRestore.whatExported')}</h5>
        <ul className="list-group mb-4">
          {ALL_SECTIONS.map((s) => (
            <li key={s.value} className="list-group-item py-2">
              <i className="ti ti-circle-check text-success me-2" />
              {SECTION_KEY_MAP[s.value] ? t(SECTION_KEY_MAP[s.value]) : s.label}
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-6">
        <h5 className="mb-3">{t('saveRestore.encryptionPassword')}</h5>
        <p className="text-muted small mb-3">
          {t('saveRestore.encryptionHint')}
        </p>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label" htmlFor="dl-password">
              {t('saveRestore.passwordLabel')}
            </label>
            <div className="input-group">
              <input
                id="dl-password"
                type={showPassword ? 'text' : 'password'}
                className="form-control"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={8}
                required
                autoComplete="new-password"
              />
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
              >
                <i className={`ti ${showPassword ? 'ti-eye-off' : 'ti-eye'}`} />
              </button>
            </div>
            <div className="form-text">{t('saveRestore.passwordMinHint')}</div>
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="dl-password-confirm">
              {t('saveRestore.confirmPasswordLabel')}
            </label>
            <input
              id="dl-password-confirm"
              type={showPassword ? 'text' : 'password'}
              className={`form-control ${
                passwordConfirm && password !== passwordConfirm ? 'is-invalid' : ''
              }`}
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              required
              autoComplete="new-password"
            />
            {passwordConfirm && password !== passwordConfirm && (
              <div className="invalid-feedback">{t('saveRestore.passwordsDoNotMatch')}</div>
            )}
          </div>

          {error && <div className="alert alert-danger py-2">{error}</div>}
          {success && <div className="alert alert-success py-2">{success}</div>}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading || !isValid}
          >
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                {t('saveRestore.exporting')}
              </>
            ) : (
              <>
                <i className="ti ti-download me-2" />
                {t('saveRestore.downloadBackup')}
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default DownloadSettingsTab;
